import { Loader2, RotateCcw, Search } from 'lucide-react';
import { type FormEvent } from 'react';
import { cn } from '@/lib/cn';
import type { BusinessClientSummary, DelayDeliveryLookupInput } from '@/types';
import { ClientSelect } from '@/features/bet-inspection/components/ClientSelect';

export interface DeliveryFilters {
  psid: string;
  transactionId: string;
  trUniqId: string;
  cycleUniqId: string;
  action: '' | 'bet' | 'win' | 'rollback';
  status: 'all' | 'pending' | 'delivered' | 'archived';
  minTries: string;
  limit: string;
}

export const EMPTY_FILTERS: DeliveryFilters = {
  psid: '',
  transactionId: '',
  trUniqId: '',
  cycleUniqId: '',
  action: '',
  status: 'pending',
  minTries: '',
  limit: '100',
};

/** Filters → the wire shape. Blank fields are omitted, never sent as ''. */
export function toInput(f: DeliveryFilters): DelayDeliveryLookupInput {
  const input: DelayDeliveryLookupInput = {};
  if (f.psid.trim()) input.play_service_id = f.psid.trim();
  if (f.transactionId.trim()) input.transaction_id = f.transactionId.trim();
  else if (f.trUniqId.trim()) input.tr_uniq_id = f.trUniqId.trim();
  else if (f.cycleUniqId.trim()) input.cycle_uniq_id = f.cycleUniqId.trim();
  if (f.action) input.action = f.action;
  if (f.status !== 'all') input.status = f.status;
  const tries = Number(f.minTries);
  if (Number.isFinite(tries) && tries > 0) input.min_tries = tries;
  const limit = Number(f.limit);
  if (Number.isFinite(limit) && limit > 0) input.limit = limit;
  return input;
}

/**
 * The same command-bar language as the console's other views.
 *
 * ⚠️ The filters mirror the table's INDEXES, not a wishlist: an id (resolved to
 * `cycle_uniq_id`/`tr_uniq_id` server-side) plus `action` is what the table is
 * indexed on. Status, tries and project only narrow what the index already
 * found — which is why the bar groups them apart and why `limit` is always
 * present: with no id this is a bounded recent scan.
 */
export function FilterBar({
  value,
  onChange,
  onSearch,
  onReset,
  loading,
  clients,
  clientsLoading,
  clientsError,
}: {
  value: DeliveryFilters;
  onChange: (patch: Partial<DeliveryFilters>) => void;
  onSearch: () => void;
  onReset: () => void;
  loading: boolean;
  clients: BusinessClientSummary[];
  clientsLoading: boolean;
  clientsError: boolean;
}) {
  const hasId = Boolean(
    value.transactionId.trim() || value.trUniqId.trim() || value.cycleUniqId.trim(),
  );
  // Either an id (index lookup) or a project (bounded scan) — never neither.
  const canSearch = hasId || Boolean(value.psid.trim());

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (canSearch) onSearch();
  };

  return (
    <form onSubmit={submit} className="flex flex-wrap items-center gap-1.5 border-b border-hairline px-4 py-2.5">
      <ClientSelect
        value={value.psid}
        onChange={(psid) => onChange({ psid })}
        clients={clients}
        loading={clientsLoading}
        error={clientsError}
      />
      <Field
        label="transaction_id"
        value={value.transactionId}
        onChange={(transactionId) => onChange({ transactionId })}
        width="w-[180px]"
      />
      <Field
        label="tr_uniq_id"
        value={value.trUniqId}
        onChange={(trUniqId) => onChange({ trUniqId })}
        width="w-[240px]"
      />
      <Field
        label="cycle_uniq_id"
        value={value.cycleUniqId}
        onChange={(cycleUniqId) => onChange({ cycleUniqId })}
        width="w-[240px]"
      />

      <Select
        label="action"
        value={value.action}
        onChange={(action) => onChange({ action: action as DeliveryFilters['action'] })}
        options={[
          { value: '', label: 'any' },
          { value: 'win', label: 'win' },
          { value: 'rollback', label: 'rollback' },
          { value: 'bet', label: 'bet' },
        ]}
        width="w-[140px]"
      />
      <Select
        label="status"
        value={value.status}
        onChange={(status) => onChange({ status: status as DeliveryFilters['status'] })}
        options={[
          { value: 'pending', label: 'undelivered' },
          { value: 'delivered', label: 'delivered' },
          { value: 'archived', label: 'archived' },
          { value: 'all', label: 'all' },
        ]}
        width="w-[160px]"
      />
      <Field
        label="min tries"
        value={value.minTries}
        onChange={(minTries) => onChange({ minTries })}
        width="w-[110px]"
      />
      <Field
        label="limit"
        value={value.limit}
        onChange={(limit) => onChange({ limit })}
        width="w-[100px]"
      />

      <button
        type="button"
        onClick={onReset}
        title="Clear all filters"
        className="inline-flex items-center gap-1.5 border border-hairline px-2.5 py-1.5 font-condensed text-[11px] font-semibold uppercase tracking-wider text-text-dim hover:border-hairline-strong hover:text-text"
      >
        <RotateCcw size={12} /> Reset
      </button>
      <button
        type="submit"
        disabled={!canSearch || loading}
        className={cn(
          'inline-flex items-center gap-1.5 border border-accent/40 bg-accent/10 px-3 py-1.5',
          'font-condensed text-[11px] font-semibold uppercase tracking-wider text-accent',
          'hover:bg-accent/20 disabled:cursor-not-allowed disabled:opacity-40',
        )}
      >
        {loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
        Search
      </button>

      <span className="ml-auto hidden text-[11px] text-text-faint xl:block">
        {hasId
          ? 'An id returns every retry row of that whole bet.'
          : 'No id — showing the most recent rows for the project.'}
      </span>
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  width,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  width: string;
}) {
  return (
    <label className={cn('relative block', width)}>
      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 font-condensed text-[9px] uppercase tracking-wider text-text-faint">
        {label}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        autoComplete="off"
        className={cn(
          'w-full border border-hairline bg-surface-sunken py-1.5 pr-2',
          'font-mono text-[12px] text-text placeholder:text-text-faint',
          'hover:border-hairline-strong focus:border-accent focus:outline-none',
        )}
        style={{ paddingLeft: `${label.length * 5 + 16}px` }}
      />
    </label>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
  width,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  width: string;
}) {
  return (
    <label className={cn('relative block', width)}>
      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 font-condensed text-[9px] uppercase tracking-wider text-text-faint">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'w-full appearance-none border border-hairline bg-surface-sunken py-1.5 pr-2',
          'font-mono text-[12px] text-text hover:border-hairline-strong focus:border-accent focus:outline-none',
        )}
        style={{ paddingLeft: `${label.length * 5 + 16}px` }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-surface text-text">
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
